import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, PhoneCall, Quote } from "lucide-react";

// Ink & Brass palette — a private-library aesthetic. Ink = deep slate/charcoal
// backdrop. Brass = warm antique-gold hairlines and letterforms.
const INK = "#0B0D10";
const INK_ELEVATED = "#12151A";
const BRASS = "#B08D57";
const BRASS_SOFT = "#C8A76A";
const PARCHMENT = "#EDE2C7";

// Sample activity — each line is time-stamped and grounded in the local
// Marion / Delaware / Caledonia / Ashley corridor so a prospect immediately
// recognizes the geography.
const ACTIVITY_LINES = [
  "06:42 AM · Marion, OH · Intake logged · Waldo Tire — first-shift caller",
  "07:11 AM · Delaware, OH · Voicemail transcribed · quote requested",
  "07:38 AM · Caledonia, OH · Weekend Agenda opt-in · +3 subscribers",
  "08:04 AM · Ashley, OH · Text-back sequence · appointment held",
  "08:29 AM · Marion, OH · After-hours inquiry · handed off to owner",
  "08:57 AM · Delaware, OH · Follow-up drafted · awaiting signoff",
  "09:15 AM · Caledonia, OH · Site 22 propane refill scheduled",
  "09:33 AM · Ashley, OH · Review response queued · 5-star",
];

const CAPABILITIES = [
  {
    title: "Front Desk",
    body: "Every call, every text, every walk-in — captured, classified, and answered before the office lights come on.",
  },
  {
    title: "Follow-Up",
    body: "The email you meant to send yesterday? Drafted last night. The review you meant to reply to? Already replied.",
  },
  {
    title: "The Books",
    body: "Invoices reconciled, deposits noted, receipts filed. Not accounting — just the noise that shouldn't touch your morning.",
  },
];

export default function FoyerPreview() {
  return (
    <div
      className="min-h-screen text-white font-inter relative"
      style={{ background: INK }}
      data-testid="foyer-page"
    >
      <InkBackdrop />

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-6 pb-24">
        {/* Header */}
        <header className="flex items-center justify-between mb-14 sm:mb-20">
          <Link
            to="/"
            data-testid="foyer-back-home"
            className="inline-flex items-center gap-2.5"
          >
            <span
              className="w-8 h-8 rounded-sm border flex items-center justify-center"
              style={{ borderColor: `${BRASS}88`, background: `${BRASS}12` }}
              aria-hidden="true"
            >
              <span
                className="font-serif-display text-[15px] italic leading-none"
                style={{ color: BRASS_SOFT }}
              >
                A
              </span>
            </span>
            <span className="leading-tight">
              <span
                className="block font-serif-display text-[15px] tracking-wide"
                style={{ color: PARCHMENT }}
              >
                Alfred
              </span>
              <span
                className="block text-[8px] uppercase tracking-[0.32em]"
                style={{ color: BRASS }}
              >
                Est. 2026
              </span>
            </span>
          </Link>

          <div
            className="flex items-center gap-2 text-[9px] sm:text-[10px] uppercase tracking-[0.32em]"
            style={{ color: BRASS }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full light-pulse"
              style={{ backgroundColor: BRASS }}
            />
            Foyer · Preview
          </div>
        </header>

        {/* Hero */}
        <section
          className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-12 lg:gap-16 items-start"
          data-testid="foyer-hero"
        >
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] uppercase tracking-[0.32em] mb-8"
              style={{
                background: "rgba(176,141,87,0.08)",
                border: `1px solid ${BRASS}55`,
                color: BRASS,
              }}
              data-testid="foyer-eyebrow"
            >
              A quiet operating partner
            </div>

            <h1
              className="font-serif-display text-5xl sm:text-6xl lg:text-[76px] leading-[1.02] tracking-tight mb-6"
              style={{ color: PARCHMENT }}
              data-testid="foyer-headline"
            >
              The office runs itself
              <br />
              <span className="italic" style={{ color: BRASS_SOFT }}>
                before you finish your coffee.
              </span>
            </h1>

            <p
              className="text-base sm:text-lg leading-relaxed max-w-xl mb-4"
              style={{ color: "#CBD5E1" }}
              data-testid="foyer-subheadline"
            >
              Built for operators like <span style={{ color: PARCHMENT }}>Amanda</span> — who
              run the front desk, the follow-up, and the books all before noon.
            </p>
            <p
              className="text-[15px] leading-relaxed max-w-xl"
              style={{ color: "#94A3B8" }}
            >
              Alfred is the quiet second set of hands that keeps a small
              operation from cracking. No dashboards to learn. No shift to
              cover. Just the work you meant to do — already done.
            </p>

            {/* CTAs */}
            <div
              className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
              data-testid="foyer-ctas"
            >
              <a
                href="/local"
                data-testid="foyer-cta-primary"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm font-serif-display text-[15px] tracking-wide transition-transform hover:translate-y-[-1px]"
                style={{
                  background: `linear-gradient(135deg, ${BRASS} 0%, #8A6A3E 100%)`,
                  color: INK,
                  boxShadow: `0 18px 40px -18px ${BRASS}90`,
                  border: `1px solid ${BRASS_SOFT}`,
                }}
              >
                Request an Introduction <ArrowRight size={15} />
              </a>
              <a
                href="/local"
                data-testid="foyer-cta-audit"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm text-[13px] uppercase tracking-[0.24em] transition-colors"
                style={{
                  background: "transparent",
                  color: BRASS_SOFT,
                  border: `1px solid ${BRASS}66`,
                }}
              >
                <ClipboardCheck size={14} /> Run Your 60-Second Audit
              </a>
            </div>

            <div
              className="mt-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]"
              style={{ color: "#64748B" }}
            >
              <PhoneCall size={11} style={{ color: BRASS }} />
              By invitation · Independent operators only
            </div>
          </div>

          {/* Amanda testimonial card */}
          <AmandaCard />
        </section>

        {/* Ornament divider */}
        <Divider />

        {/* Capabilities row */}
        <section
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          data-testid="foyer-capabilities"
        >
          {CAPABILITIES.map((c, i) => (
            <div
              key={c.title}
              className="rounded-sm p-6 relative"
              style={{
                background: INK_ELEVATED,
                border: `1px solid rgba(176,141,87,0.18)`,
              }}
              data-testid={`foyer-cap-${i}`}
            >
              <div
                className="absolute inset-x-0 top-0 h-px"
                style={{
                  background: `linear-gradient(90deg, transparent, ${BRASS}, transparent)`,
                }}
              />
              <div
                className="text-[9px] uppercase tracking-[0.32em] mb-3"
                style={{ color: BRASS }}
              >
                Function {String(i + 1).padStart(2, "0")}
              </div>
              <div
                className="font-serif-display text-2xl mb-2"
                style={{ color: PARCHMENT }}
              >
                {c.title}
              </div>
              <p className="text-[14px] leading-relaxed text-slate-400">
                {c.body}
              </p>
            </div>
          ))}
        </section>

        <Divider />

        {/* Live Activity Ledger */}
        <LiveActivityLedger />

        <Divider />

        {/* Signoff */}
        <section className="text-center pt-4">
          <div
            className="text-[10px] uppercase tracking-[0.36em] mb-3"
            style={{ color: BRASS }}
          >
            The house closes at 6. Alfred does not.
          </div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-slate-600">
            Ink &amp; Brass · Foyer Edition · Preview
          </div>
        </section>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Backdrop --- */

function InkBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(ellipse at 20% 15%, rgba(176,141,87,0.10) 0%, transparent 55%),
             radial-gradient(ellipse at 85% 85%, rgba(176,141,87,0.06) 0%, transparent 60%)`,
        }}
      />
      {/* Fine parchment texture (dot noise) */}
      <div
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(rgba(200,167,106,0.6) 1px, transparent 1px)",
          backgroundSize: "4px 4px",
        }}
      />
      {/* Bottom brass hairline */}
      <div
        className="absolute inset-x-0 bottom-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${BRASS}55 50%, transparent 95%)`,
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------- Ornament Divider --- */

function Divider() {
  return (
    <div
      className="my-16 sm:my-20 flex items-center gap-4"
      aria-hidden="true"
    >
      <span
        className="h-px flex-1"
        style={{
          background: `linear-gradient(90deg, transparent, ${BRASS}55)`,
        }}
      />
      <span
        className="w-2 h-2 rotate-45"
        style={{ background: BRASS_SOFT, boxShadow: `0 0 8px ${BRASS}88` }}
      />
      <span
        className="h-px flex-1"
        style={{
          background: `linear-gradient(90deg, ${BRASS}55, transparent)`,
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------- Amanda card ------- */

function AmandaCard() {
  return (
    <aside
      className="relative rounded-sm p-6 sm:p-7 self-start"
      style={{
        background: INK_ELEVATED,
        border: `1px solid ${BRASS}44`,
        boxShadow: `0 30px 60px -30px rgba(0,0,0,0.7), 0 0 0 1px ${BRASS}11 inset`,
      }}
      data-testid="foyer-amanda-card"
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${BRASS}, transparent)`,
        }}
      />
      <Quote size={20} style={{ color: BRASS }} className="mb-4" />
      <p
        className="font-serif-display text-lg sm:text-xl italic leading-relaxed mb-5"
        style={{ color: PARCHMENT }}
      >
        &ldquo;I ran the phones, the invoices, the follow-ups, and the coffee.
        Alfred took three of those before I sat down.&rdquo;
      </p>
      <div className="flex items-center gap-3 pt-4 border-t border-white/5">
        <div
          className="w-9 h-9 rounded-full inline-flex items-center justify-center font-serif-display text-[13px]"
          style={{
            background: `${BRASS}18`,
            color: BRASS_SOFT,
            border: `1px solid ${BRASS}55`,
          }}
          aria-hidden="true"
        >
          A
        </div>
        <div className="leading-tight">
          <div
            className="font-serif-display text-[15px]"
            style={{ color: PARCHMENT }}
          >
            Amanda
          </div>
          <div
            className="text-[10px] uppercase tracking-[0.28em] mt-0.5"
            style={{ color: BRASS }}
          >
            Front desk · Follow-up · The books
          </div>
        </div>
      </div>
    </aside>
  );
}

/* --------------------------------------------------- Live Activity ------ */

function LiveActivityLedger() {
  const items = [...ACTIVITY_LINES, ...ACTIVITY_LINES];
  return (
    <section data-testid="foyer-live-activity">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div
            className="text-[9px] uppercase tracking-[0.34em]"
            style={{ color: BRASS }}
          >
            The Ledger
          </div>
          <div
            className="font-serif-display text-2xl sm:text-3xl mt-1"
            style={{ color: PARCHMENT }}
          >
            This morning, in the corridor.
          </div>
        </div>
        <div
          className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em]"
          style={{ color: BRASS_SOFT }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full light-pulse"
            style={{ backgroundColor: BRASS_SOFT }}
          />
          Live
        </div>
      </div>

      <div
        className="rounded-sm p-4 sm:p-5 h-72 flex flex-col"
        style={{
          background: INK_ELEVATED,
          border: `1px solid ${BRASS}22`,
        }}
      >
        <div className="flex-1 overflow-hidden relative">
          <div
            className="absolute inset-0"
            style={{
              maskImage:
                "linear-gradient(180deg, transparent 0%, black 12%, black 88%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(180deg, transparent 0%, black 12%, black 88%, transparent 100%)",
            }}
          >
            <div
              className="sentry-marquee font-mono-aviation text-[12px] sm:text-[13px] leading-relaxed"
              style={{ color: "#CBD5E1" }}
              data-testid="foyer-ledger-marquee"
            >
              {items.map((line, i) => (
                <div key={i} className="py-2 flex items-start gap-3">
                  <span
                    className="mt-1.5 w-1 h-1 rounded-full shrink-0"
                    style={{ backgroundColor: BRASS_SOFT }}
                  />
                  <span>{line}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
